import upperFirst from 'lodash/upperFirst';
import { ThemeProvider } from 'styled-components';
import { Page, PageBreadcrumb, PageContent, Tabs, TextButton } from '@zillow/constellation';
import { Col, CometTheme, Row } from '@zg-rentals/zrs-comet';
import { PDP_HEADER_TAB_LABEL, TAB_PATHS } from './constants/index';
import { StyledLink, StyledPageWrapper, StyledTabsList } from './styles';
import PhotoGridPDP from '../PhotoGridPDP';
import type { TabConfig } from '../PDPHeader/PDPHeader';
import type { Listing } from '../declarations/Listing';

interface Router {
  transitionTo: (url: string) => void;
}

export interface Props {
  displayAnalytics?: boolean;
  displayApplications?: boolean;
  displayLeases?: boolean;
  displayMessages?: boolean; // changed from contacts to messages
  displayPayments?: boolean;
  handleBreadcrumbClick?: (event?: Event) => void;
  handleEditClick?: (event?: Event) => void;
  hideTabList?: boolean;
  isInMobileWebView: boolean;
  listing: Listing;
  pathname: string;
  signMapUrl?: () => Promise<string>;
  tabConfig: TabConfig;
}

export interface State {
  tabList: Array<string>;
}

const defaultProps = {
  isInMobileWebView: false,
  hideTabList: false,
  displayAnalytics: false,
  displayMessages: false,
  displayApplications: false,
  displayLeases: false,
  displayPayments: false,
};

const interpolatePath = (tabName: string, propertyId: string) => TAB_PATHS[tabName].replace(':propertyId', propertyId);

export const loadTab = (tabName: string, tabConfig: TabConfig, propertyId: string) => {
  const configItem = tabConfig[tabName];
  const hasTabPath = configItem && Object.prototype.hasOwnProperty.call(configItem, 'tabPath');
  // return toUrl for unit tests
  const toUrl = interpolatePath(tabName, propertyId);

  // custom tab path is set
  if (hasTabPath) {
    const { tabPath: toUrl } = configItem;
    if (toUrl) window.location.assign(toUrl);
    return toUrl;
  }

  // RMW has a window.router property to handle SPA page transitions
  if ('router' in window && window.router) {
    (window.router as Router).transitionTo(toUrl);
  } else {
    window.location.assign(toUrl);
  }

  return toUrl;
};

const PDPWithMap = ({
  displayAnalytics,
  displayApplications,
  displayLeases,
  displayMessages,
  displayPayments,
  handleBreadcrumbClick,
  handleEditClick,
  hideTabList,
  isInMobileWebView,
  listing,
  pathname,
  signMapUrl,
  tabConfig,
}: Props) => {
  const {
    propertyId,
    restrictions: {
      applications,
      isPaymentsRestricted,
      isLeasesRestricted,
      isAnalyticsRestricted,
      isReviewsRestricted,
      isContactsRestricted,
    },
  } = listing;

  const displayMap = {
    [PDP_HEADER_TAB_LABEL.LISTING]: true,
    [PDP_HEADER_TAB_LABEL.MESSAGES]: !isContactsRestricted || displayMessages,
    [PDP_HEADER_TAB_LABEL.APPLICATIONS]:
      (applications && applications.isAccessRestricted === false) || displayApplications,
    [PDP_HEADER_TAB_LABEL.LEASES]: !isLeasesRestricted || displayLeases,
    [PDP_HEADER_TAB_LABEL.PAYMENTS]: !isPaymentsRestricted || displayPayments,
    [PDP_HEADER_TAB_LABEL.ANALYTICS]: !isAnalyticsRestricted || displayAnalytics,
  };

  const tabList = Object.values(PDP_HEADER_TAB_LABEL).filter((label) => displayMap[label] || false);

  const getInitialSelectedTab = (): string => {
    const parts = pathname.split('/');

    let selectedTabName = '';

    parts.some((tabName: string) => {
      if (tabName === 'leasing') {
        // leases pathname is /rental-manager/leasing/property/:propertyId
        // the tab name part needs to be renamed to match the tab list set via PDP_HEADER_TAB_LABEL
        tabName = PDP_HEADER_TAB_LABEL.LEASES;
      }

      const found = tabList.indexOf(tabName) >= 0;

      if (found) {
        selectedTabName = tabName;
      }

      return found;
    });

    return selectedTabName;
  };

  const handleTabClick = (selectedTab: string) => {
    loadTab(selectedTab, tabConfig, propertyId);
  };

  const selectedTab = getInitialSelectedTab();
  const configItem = tabConfig[selectedTab];

  return (
    <ThemeProvider theme={CometTheme}>
      <Page backgroundColor="transparent" className="PDPHeader-Container" data-testid="PDPWithMap">
        {!isInMobileWebView && (
          <PageBreadcrumb
            backButton={
              <TextButton as="a" href="/rental-manager/properties" onClick={handleBreadcrumbClick as any}>
                My properties
              </TextButton>
            }
            data-testid="PageBreadcrumb"
          />
        )}

        <PageContent>
          <Row hidden={[]}>
            <Col xs={12}>
              <StyledPageWrapper>
                {!isInMobileWebView && (
                  <PhotoGridPDP
                    data-testid="PhotoGridPDP"
                    listing={listing}
                    onClickEdit={handleEditClick && handleEditClick}
                    signMapUrl={signMapUrl}
                  />
                )}
                <Tabs selected={selectedTab} onSelected={handleTabClick}>
                  {!hideTabList && !isInMobileWebView && (
                    <StyledTabsList data-testid="StyledTabsList">
                      {tabList.map((tabName, i) => (
                        <Tabs.Tab key={`${tabName}-${i}`} id={`${tabName}`} data-test={`${tabName}`}>
                          <StyledLink href={interpolatePath(tabName, propertyId)} selected={selectedTab === tabName}>
                            {upperFirst(tabName)}
                          </StyledLink>
                        </Tabs.Tab>
                      ))}
                    </StyledTabsList>
                  )}
                  <Tabs.Panels>
                    {configItem && <Tabs.Panel aria-labelledby={selectedTab}>{configItem.tabComponent}</Tabs.Panel>}
                  </Tabs.Panels>
                </Tabs>
              </StyledPageWrapper>
            </Col>
          </Row>
        </PageContent>
      </Page>
    </ThemeProvider>
  );
};

PDPWithMap.defaultProps = defaultProps;

export { PDP_HEADER_TAB_LABEL };
export default PDPWithMap;
